/**
 * Penilaian Individu (360-degree assessment) types
 */

export interface SSQuestion {
  id: string
  indicator_id: string
  question_text: string
  order_no: number
  is_active: boolean
}

export interface SSIndicator {
  id: string
  name: string
  description: string | null
  order_no: number
  bobot: number
  is_active: boolean
  questions: SSQuestion[]
}

export interface AkhlakBehavior {
  id: string
  value_id: string
  behavior_text: string
  order_no: number
  is_active: boolean
}

export interface AkhlakValue {
  id: string
  code: string
  name: string
  description: string | null
  order_no: number
  is_active: boolean
  behaviors: AkhlakBehavior[]
}

// ─── Master Data CRUD input types ────────────────────────────────────────────

export interface SSIndicatorCreate {
  name: string
  description?: string | null
  order_no: number
  bobot: number  // fraction 0–1
}

export interface SSIndicatorUpdate {
  name?: string
  description?: string | null
  order_no?: number
  bobot?: number
  is_active?: boolean
}

export interface SSQuestionCreate {
  question_text: string
  order_no: number
}

export interface SSQuestionUpdate {
  question_text?: string
  order_no?: number
  is_active?: boolean
}

export interface AkhlakValueCreate {
  code: string
  name: string
  description?: string | null
  order_no: number
}

export interface AkhlakValueUpdate {
  code?: string
  name?: string
  description?: string | null
  order_no?: number
  is_active?: boolean
}

export interface AkhlakBehaviorCreate {
  behavior_text: string
  order_no: number
}

export interface AkhlakBehaviorUpdate {
  behavior_text?: string
  order_no?: number
  is_active?: boolean
}

export type PeriodStatus = 'draft' | 'open' | 'closed'
export type AssessorRole = 'ATASAN' | 'PEER' | 'KOMITE' | 'BAWAHAN'
export type AssignmentStatus = 'pending' | 'in_progress' | 'submitted'
export type ResultStatus =
  | 'pending'
  | 'atasan1_review'
  | 'atasan1_approved'
  | 'approved'
  | 'rejected'

export interface AssessmentPeriod {
  id: string
  quarter: number
  year: number
  name: string
  start_date: string
  end_date: string
  submission_deadline: string | null
  status: PeriodStatus
  created_at: string
}

export interface HSQuestion {
  id: string
  kpi_id: string
  question_text: string
  order_no: number
  is_active: boolean
}

export interface KPIDefinition {
  id: string
  period_id: string
  name: string
  target_description: string
  annual_target: string | null
  kpi_type: 'company' | 'individual'
  order_no: number
  department: string | null
  is_active: boolean
  hs_questions: HSQuestion[]
}

export interface KPIAssignment {
  id: string
  employee_id: string
  kpi_id: string
  period_id: string
  bobot: number
  target_value: string | null
  realisasi: string | null
  realisasi_pct: number | null
  realisasi_comment: string | null
  kpi: KPIDefinition | null
}

export interface UserBasic {
  id: string
  display_name: string | null
  email: string
  department: string | null
  job_title: string | null
}

export interface PeriodBasic {
  id: string
  name: string
  status: string
}

export interface PeerAssignment {
  id: string
  period_id: string
  assessor_id: string
  assessee_id: string
  assessor_role: AssessorRole
  status: AssignmentStatus
  submitted_at: string | null
  assessor: UserBasic | null
  period: PeriodBasic | null
  assessee: UserBasic | null
}

export interface AssessmentApproval {
  id: string
  result_id: string
  approver_id: string
  level: number
  status: 'pending' | 'approved' | 'rejected'
  comment: string | null
  approved_at: string | null
  approver: UserBasic | null
}

export interface AssessmentResult {
  id: string
  employee_id: string
  period_id: string
  hs_score: number | null
  ss_score: number | null
  akhlak_score: number | null
  final_score: number | null
  hr_final_score: number | null
  komentar_pekerja: string | null
  komentar_penilai: string | null
  usulan_pengembangan: string | null
  approval_status: ResultStatus
  scores_computed_at: string | null
  published_at: string | null
  employee: UserBasic | null
  approvals: AssessmentApproval[]
}

// ==================== Input types ====================

export interface CreatePeriodInput {
  quarter: number
  year: number
  start_date: string
  end_date: string
  submission_deadline?: string | null
}

export interface UpdatePeriodInput {
  start_date?: string
  end_date?: string
  submission_deadline?: string | null
  status?: PeriodStatus
}

export interface CreateKPIInput {
  period_id: string
  name: string
  target_description: string
  kpi_type: 'company' | 'individual'
  order_no: number
  department?: string | null
  hs_questions?: { question_text: string; order_no: number }[]
}

export interface AssignKPIInput {
  employee_id: string
  kpi_id: string
  period_id: string
  bobot: number
  target_value?: string | null
}

export interface UpdateKPIDefinitionInput {
  name?: string
  target_description?: string
  annual_target?: string | null
}

export interface UpdateKPIAssignmentInput {
  bobot?: number
  target_value?: string | null
}

export interface UpdateRealisasiInput {
  realisasi: string
  realisasi_pct?: number | null
  realisasi_comment?: string | null
}

export interface PeerAssignmentItem {
  assessor_id: string
  assessee_id: string
  assessor_role: AssessorRole
}

export interface BulkCreateAssignmentsInput {
  period_id: string
  assignments: PeerAssignmentItem[]
}

export interface HSResponseItem {
  hs_question_id: string
  kpi_assignment_id: string
  score: number
}

export interface SSResponseItem {
  ss_question_id: string
  score: number
}

export interface AkhlakResponseItem {
  behavior_id: string
  score: number
  tindakan_aktual?: string | null
}

export interface NoteItem {
  note_type: 'ss' | 'akhlak' | 'kpi'
  reference_id: string
  note_text?: string | null
}

export interface SavedHSResponse {
  hs_question_id: string
  kpi_assignment_id: string
  score: number
}

export interface SavedSSResponse {
  ss_question_id: string
  score: number
}

export interface SavedAkhlakResponse {
  behavior_id: string
  score: number
  tindakan_aktual: string | null
}

export interface SavedNote {
  note_type: string
  reference_id: string
  note_text: string | null
}

export interface AssignmentResponses {
  hs_responses: SavedHSResponse[]
  ss_responses: SavedSSResponse[]
  akhlak_responses: SavedAkhlakResponse[]
  notes: SavedNote[]
}

export interface SubmitAssessmentInput {
  hs_responses: HSResponseItem[]
  ss_responses: SSResponseItem[]
  akhlak_responses: AkhlakResponseItem[]
  notes: NoteItem[]
  is_draft: boolean
}

export interface UpdateNarrativeInput {
  komentar_pekerja?: string | null
  komentar_penilai?: string | null
  usulan_pengembangan?: string | null
  hr_final_score?: number | null
}

export interface ApprovalActionInput {
  action: 'approve' | 'reject'
  comment?: string | null
}

// ==================== Response wrappers ====================

export interface PeriodsResponse {
  data: AssessmentPeriod[]
  total: number
}

export interface AssignmentsResponse {
  data: PeerAssignment[]
  total: number
}

export interface ResultsResponse {
  data: AssessmentResult[]
  total: number
}

// ==================== Employee overview (admin assignment view) ====================

export interface EmployeeAssignmentSummary {
  id: string
  assessor_id: string
  assessor_name: string | null
  assessor_role: AssessorRole
  status: AssignmentStatus
  submitted_at: string | null
}

export interface EmployeeOverviewRow {
  employee: {
    id: string
    display_name: string | null
    full_name: string | null
    email: string
    job_title: string | null
    department: string | null
    division_id: string | null
    division_name: string | null
    manager_id: string | null
    manager_name: string | null
  }
  assignments: EmployeeAssignmentSummary[]
}

export interface EmployeeOverviewResponse {
  data: EmployeeOverviewRow[]
  total: number
}

// ==================== Scoring helpers ====================

export const HS_SCORE_LABELS: Record<number, string> = {
  5: 'Excellent',
  4: 'Good',
  3: 'Satisfactory',
  2: 'Needs Improvement',
  1: 'Unsatisfactory',
}

export const SS_SCORE_LABELS: Record<number, string> = {
  4: 'Excellent',
  3: 'Satisfactory',
  2: 'Needs Improvement',
  1: 'Unsatisfactory',
}

export const AKHLAK_SCORE_LABELS: Record<number, string> = {
  4: 'Selalu',
  3: 'Sering',
  2: 'Jarang',
  1: 'Tidak Pernah',
}

export const QUARTER_NAMES: Record<number, string> = {
  1: 'I',
  2: 'II',
  3: 'III',
  4: 'IV',
}

export const RESULT_STATUS_LABELS: Record<ResultStatus, string> = {
  pending: 'Menunggu Review',
  atasan1_review: 'Menunggu Review',
  atasan1_approved: 'Menunggu Review',
  approved: 'Final Disetujui',
  rejected: 'Ditolak',
}

export const RESULT_STATUS_COLORS: Record<ResultStatus, string> = {
  pending: 'bg-gray-100 text-gray-700',
  atasan1_review: 'bg-yellow-100 text-yellow-700',
  atasan1_approved: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
}

// ==================== KPI Plan ====================

export type KPIPlanStatus = 'draft' | 'pending_manager' | 'pending_hr' | 'approved' | 'rejected_by_manager' | 'rejected_by_hr'

export interface KPIPlan {
  id: string
  employee_id: string
  year: number
  status: KPIPlanStatus
  manager_comment: string | null
  approved_by: string | null
  approved_at: string | null
}

export interface TeamKPIPlanRow {
  employee: UserBasic
  plan: KPIPlan | null
}

export interface CreateMyKPIInput {
  employee_id: string
  year: number
  name: string
  target_description: string
  annual_target?: string | null
  bobot: number
  q1_target?: string | null
  q2_target?: string | null
  q3_target?: string | null
  q4_target?: string | null
}

// ==================== Company KPI (global, assigned to all employees) ====================

export interface CompanyKPIRow {
  name: string
  target_description: string
  annual_target: string | null
  bobot: number
  employee_count: number
  q1_kpi_id: string | null
  q2_kpi_id: string | null
  q3_kpi_id: string | null
  q4_kpi_id: string | null
  q1_target: string | null
  q2_target: string | null
  q3_target: string | null
  q4_target: string | null
  q1_realisasi: string | null
  q2_realisasi: string | null
  q3_realisasi: string | null
  q4_realisasi: string | null
  q1_realisasi_pct: number | null
  q2_realisasi_pct: number | null
  q3_realisasi_pct: number | null
  q4_realisasi_pct: number | null
}

export interface UpdateCompanyKPIRealisasiInput {
  name: string
  year: number
  quarter: number
  realisasi: string
  realisasi_pct?: number | null
}

export interface CreateCompanyKPIInput {
  year: number
  name: string
  target_description: string
  annual_target?: string | null
  bobot: number
  q1_target?: string | null
  q2_target?: string | null
  q3_target?: string | null
  q4_target?: string | null
}

export interface UpdateCompanyKPIInput {
  name: string
  target_description: string
  annual_target?: string | null
  bobot: number
  q1_target?: string | null
  q2_target?: string | null
  q3_target?: string | null
  q4_target?: string | null
}

export const KPI_PLAN_STATUS_LABELS: Record<KPIPlanStatus, string> = {
  draft: 'Draft',
  pending_manager: 'Menunggu Atasan',
  pending_hr: 'Menunggu HR',
  approved: 'Disetujui',
  rejected_by_manager: 'Ditolak Atasan',
  rejected_by_hr: 'Ditolak HR',
}

export const KPI_PLAN_STATUS_COLORS: Record<KPIPlanStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  pending_manager: 'bg-yellow-100 text-yellow-700',
  pending_hr: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  rejected_by_manager: 'bg-red-100 text-red-700',
  rejected_by_hr: 'bg-red-100 text-red-700',
}
